import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const PricingTestimonialsSection = () => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  const translations = {
    en: {
      title: "What Our Customers Say About Value",
      subtitle: "Real stories of ROI and cost savings from our clients",
      testimonials: [
        {
          name: "Rajesh Kumar",
          position: "Managing Director",
          company: "Kumar Aluminum Industries",
          location: "Mumbai, Maharashtra",
          image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80",
          quote: "AlumERP paid for itself within 4 months. We saved ₹3.2 lakhs monthly just on inventory optimization. The transparent pricing was refreshing - no hidden surprises.",
          metrics: {
            savings: "₹3.2L/month",
            roi: "400%",
            payback: "4 months"
          }
        },
        {
          name: "Priya Sharma",
          position: "Operations Head",
          company: "Stellar Extrusions",
          location: "Bangalore, Karnataka",
          image: "https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80",
          quote: "The financing options made it easy to get started. MSME benefits and quarterly payments fit perfectly with our cash flow. ROI exceeded expectations.",
          metrics: {
            savings: "₹1.8L/month",
            roi: "350%",
            payback: "5 months"
          }
        },
        {
          name: "Amit Gupta",
          position: "CEO",
          company: "Precision Profiles",
          location: "Delhi NCR",
          image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80",
          quote: "Best investment decision we made. The cost-benefit analysis was spot-on. We're saving significantly on labor costs and quality control expenses.",
          metrics: {
            savings: "₹5.1L/month",
            roi: "520%",
            payback: "3 months"
          }
        }
      ]
    },
    hi: {
      title: "हमारे ग्राहक मूल्य के बारे में क्या कहते हैं",
      subtitle: "हमारे क्लाइंट्स की ROI और लागत बचत की वास्तविक कहानियां",
      testimonials: [
        {
          name: "राजेश कुमार",
          position: "प्रबंध निदेशक",
          company: "कुमार एल्यूमीनियम इंडस्ट्रीज",
          location: "मुंबई, महाराष्ट्र",
          image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80",
          quote: "AlumERP ने 4 महीने में अपना पैसा वापस कर दिया। हमने केवल इन्वेंटरी अनुकूलन पर मासिक ₹3.2 लाख बचाए। पारदर्शी मूल्य निर्धारण ताज़गी भरा था - कोई छुपे हुए आश्चर्य नहीं।",
          metrics: {
            savings: "₹3.2L/माह",
            roi: "400%",
            payback: "4 महीने"
          }
        },
        {
          name: "प्रिया शर्मा",
          position: "संचालन प्रमुख",
          company: "स्टेलर एक्सट्रूज़न",
          location: "बैंगलोर, कर्नाटक",
          image: "https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80",
          quote: "वित्तपोषण विकल्पों ने शुरुआत करना आसान बना दिया। MSME लाभ और त्रैमासिक भुगतान हमारे कैश फ्लो के साथ पूरी तरह फिट हुए। ROI अपेक्षाओं से अधिक था।",
          metrics: {
            savings: "₹1.8L/माह",
            roi: "350%",
            payback: "5 महीने"
          }
        },
        {
          name: "अमित गुप्ता",
          position: "CEO",
          company: "प्रेसिजन प्रोफाइल्स",
          location: "दिल्ली NCR",
          image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80",
          quote: "हमारा सबसे अच्छा निवेश निर्णय। लागत-लाभ विश्लेषण बिल्कुल सही था। हम श्रम लागत और गुणवत्ता नियंत्रण खर्चों पर काफी बचा रहे हैं।",
          metrics: {
            savings: "₹5.1L/माह",
            roi: "520%",
            payback: "3 महीने"
          }
        }
      ]
    }
  };

  const t = translations[currentLanguage];

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage) {
      setCurrentLanguage(savedLanguage);
    }
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % t.testimonials.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [t.testimonials.length]);

  const currentTestimonial = t.testimonials[activeTestimonial];

  return (
    <section id="pricing-testimonials" className="py-20 bg-gradient-to-r from-primary-50 to-accent-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6">
            {t.title}
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            {t.subtitle}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Main Testimonial */}
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-border">
            <div className="flex items-center mb-6">
              <img
                src={currentTestimonial.image}
                alt={currentTestimonial.name}
                className="w-16 h-16 rounded-full mr-4"
              />
              <div>
                <h3 className="text-lg font-bold text-primary">
                  {currentTestimonial.name}
                </h3>
                <p className="text-sm text-text-secondary">
                  {currentTestimonial.position}
                </p>
                <p className="text-sm text-text-secondary">
                  {currentTestimonial.company}
                </p>
                <p className="text-xs text-text-secondary">
                  {currentTestimonial.location}
                </p>
              </div>
            </div>

            <div className="mb-6">
              <Icon name="Quote" size={32} color="var(--color-primary)" className="mb-4" />
              <p className="text-gray-700 text-lg italic leading-relaxed">
                "{currentTestimonial.quote}"
              </p>
            </div>

            {/* Metrics */}
            <div className="grid grid-cols-3 gap-4 p-4 bg-surface-50 rounded-lg">
              <div className="text-center">
                <div className="text-xl font-bold text-success">
                  {currentTestimonial.metrics.savings}
                </div>
                <div className="text-xs text-text-secondary">
                  {currentLanguage === 'en' ? 'Monthly Savings' : 'मासिक बचत'}
                </div>
              </div>
              <div className="text-center">
                <div className="text-xl font-bold text-primary">
                  {currentTestimonial.metrics.roi}
                </div>
                <div className="text-xs text-text-secondary">
                  {currentLanguage === 'en' ? 'ROI Achieved' : 'ROI प्राप्त'}
                </div>
              </div>
              <div className="text-center">
                <div className="text-xl font-bold text-accent">
                  {currentTestimonial.metrics.payback}
                </div>
                <div className="text-xs text-text-secondary">
                  {currentLanguage === 'en' ? 'Payback Period' : 'पेबैक अवधि'}
                </div>
              </div>
            </div>
          </div>

          {/* Testimonial Navigation */}
          <div className="space-y-4">
            {t.testimonials.map((testimonial, index) => (
              <div
                key={index}
                className={`p-6 rounded-lg cursor-pointer transition-all duration-300 ${
                  index === activeTestimonial
                    ? 'bg-white shadow-lg border-2 border-primary'
                    : 'bg-white bg-opacity-50 hover:bg-opacity-80'
                }`}
                onClick={() => setActiveTestimonial(index)}
              >
                <div className="flex items-center space-x-4">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full"
                  />
                  <div className="flex-1">
                    <h4 className="font-bold text-primary">
                      {testimonial.name}
                    </h4>
                    <p className="text-sm text-text-secondary">
                      {testimonial.company}
                    </p>
                    <div className="flex items-center space-x-4 mt-2">
                      <div className="text-xs text-success font-medium">
                        {testimonial.metrics.savings}
                      </div>
                      <div className="text-xs text-primary font-medium">
                        {testimonial.metrics.roi}
                      </div>
                    </div>
                  </div>
                  {index === activeTestimonial && (
                    <Icon name="ChevronRight" size={20} color="var(--color-primary)" />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Progress Indicators */}
        <div className="flex justify-center mt-8 space-x-2">
          {t.testimonials.map((_, index) => (
            <button
              key={index}
              onClick={() => setActiveTestimonial(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === activeTestimonial
                  ? 'bg-primary' :'bg-white bg-opacity-50 hover:bg-opacity-80'
              }`}
            />
          ))}
        </div>

        {/* Bottom Stats */}
        <div className="mt-16 bg-white rounded-2xl p-8 shadow-lg">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-3xl font-bold text-primary mb-2">₹2.5L</div>
              <div className="text-sm text-text-secondary">
                {currentLanguage === 'en' ? 'Average Monthly Savings' : 'औसत मासिक बचत'}
              </div>
            </div>
            <div>
              <div className="text-3xl font-bold text-success mb-2">420%</div>
              <div className="text-sm text-text-secondary">
                {currentLanguage === 'en' ? 'Average ROI' : 'औसत ROI'}
              </div>
            </div>
            <div>
              <div className="text-3xl font-bold text-accent mb-2">4.2</div>
              <div className="text-sm text-text-secondary">
                {currentLanguage === 'en' ? 'Months to Payback' : 'पेबैक के लिए महीने'}
              </div>
            </div>
            <div>
              <div className="text-3xl font-bold text-primary mb-2">98%</div>
              <div className="text-sm text-text-secondary">
                {currentLanguage === 'en' ? 'Customer Satisfaction' : 'ग्राहक संतुष्टि'}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingTestimonialsSection;